import type { ClubCategory, ShotOutcome } from '@golf/common';
import { ApiError, api } from './api.ts';
import { enAttenteSur, enfiler, purgerDependants, signalerReseau } from './file.ts';
import { MAGASIN_ETAT, identifiant, lire, remplacer } from './local.ts';

export interface Session {
    id: string;
    bagId: string;
    bagName: string;
    mode: string;
    status: 'in_progress' | 'completed' | 'aborted';
    startedAt: string;
    endedAt: string | null;
    location: string | null;
    notes: string | null;
    /** Nom de l'exercice personnalisé joué, s'il y en avait un. Il prime sur
     *  le libellé du mode, qui vaudrait « Exercice libre » pour tous. */
    exerciseName?: string | null;
    /** Sa consigne, affichée pendant la saisie. */
    exerciseDescription?: string | null;
    /** Ce qui vaut UN POINT sur cet atelier — « Rentrée du premier coup ».
     *  Quand il est là, la saisie se fait à deux boutons et le compte se lit en
     *  points ; sinon, le pavé des sept issues de balle reste. */
    exerciseSuccessLabel?: string | null;
    /** Le règlement long de l'atelier, affiché pendant qu'on le fait. */
    exerciseCriteria?: string | null;
    /** Ses RÈGLES, figées à la séance : balles par tour, nombre de tours, et
     *  les types de club permis. `null` vaut « libre », comme dans l'éditeur ;
     *  une liste de types vide vaut « tout le sac ». */
    exerciseBalls?: number | null;
    exerciseRounds?: number | null;
    exerciseClubTemplateIds?: number[];
}

export interface Shot {
    id: string;
    shotIndex: number;
    outcome: ShotOutcome;
    distanceM: number | null;
}

export interface Series {
    id: string;
    seriesIndex: number;
    clubId: string;
    completedAt: string | null;
    shots: Shot[];
}

export interface ClubSummary {
    clubId: string;
    label: string;
    brand: string | null;
    loftDeg: number | null;
    category: ClubCategory;
    nickname: string | null;
    shotsTotal: number;
    seriesCount: number;
    cntCenter: number; cntSlice: number; cntHook: number; cntTop: number;
    cntFat: number; cntShank: number; cntMiss: number;
    successRate: number | null;
    distanceAvgM: number | null;
    distanceMinM: number | null;
    distanceMaxM: number | null;
    /** Distance annoncée pour ce club, en mètres. */
    referenceDistanceM: number | null;
    /** Moyenne du joueur avec ce club, toutes séances confondues. */
    careerDistanceM: number | null;
    prevStartedAt: string | null;
    prevShotsTotal: number | null;
    prevCntCenter: number | null; prevCntSlice: number | null; prevCntHook: number | null;
    prevCntTop: number | null; prevCntFat: number | null; prevCntShank: number | null;
    prevCntMiss: number | null;
    prevSuccessRate: number | null;
    prevDistanceAvgM: number | null;
}

export interface Summary {
    session: Session;
    clubs: ClubSummary[];
    totals: { shots: number; center: number; series: number; successRate: number | null };
}

/** Une ligne d'historique, quelle que soit sa nature.
 *
 *  Practice et putting ne se stockent pas dans les mêmes tables — un green n'a
 *  ni sac, ni séries, ni issues de balle — mais pour le joueur c'est la même
 *  liste. `kind` dit laquelle des deux moitiés est renseignée. */
export interface HistoryItem {
    kind: 'training' | 'putting';
    id: string;
    mode: string;
    status: string;
    startedAt: string;
    endedAt: string | null;
    /** Nom du sac, ou nom de la partie de putting. */
    title: string | null;
    location: string | null;
    notes: string | null;
    /** Balles frappées, ou trous joués. */
    count: number;
    /** Practice seulement. `numeric` de Postgres : arrive en chaîne. */
    successRate: string | null;
    /** Putting seulement. */
    avgPutts: string | null;
    playerCount: number | null;
    position: string | null;
}

export interface TrainingMode {
    mode: string;
    lastSessionAt: string | null;
    sessionCount: number;
}

/** Une fenêtre de séances, ramenée à des compteurs de balles. */
export interface StatsWindow {
    sessions: number;
    shots: number;
    center: number;
    slice: number; hook: number; top: number;
    fat: number; shank: number; miss: number;
}

export interface StatsClub {
    clubId: string;
    label: string;
    brand: string | null;
    category: ClubCategory;
    nickname: string | null;
    loftDeg: number | null;
    referenceDistanceM: number | null;
    shots: number;
    center: number;
    prevShots: number;
    prevCenter: number;
    distanceAvgM: number | null;
    distanceMinM: number | null;
    distanceMaxM: number | null;
    distanceCount: number;
    /** Les distances mesurées de la fenêtre récente, une par balle. Deux
     *  bornes donnent la largeur du groupement, jamais sa forme. */
    distances: number[];
    /** Le taux de réussite jour par jour, du plus ancien au plus récent, sur
     *  dix journées au plus. Deux points résument une progression ; une suite
     *  la montre. */
    history: { jour: string; shots: number; center: number }[];
    /** La moyenne de la fenêtre précédente, pour dire si la balle part plus
     *  loin. `null` tant qu'aucune distance n'a été mesurée avant. */
    prevDistanceAvgM: number | null;
    /** L'amplitude de la fenêtre précédente, pour dire si ça se resserre. */
    prevDistanceMinM: number | null;
    prevDistanceMaxM: number | null;
    prevDistanceCount: number;
    lastPlayedAt: string | null;
    /** Les fautes de la fenêtre récente, par type. C'est la matière du radar :
     *  un club ne se rate pas d'une seule façon. */
    slice: number;
    hook: number;
    top: number;
    fat: number;
    shank: number;
    miss: number;
}

/** Le putting du joueur, toutes parties confondues.
 *
 *  « En régulation », c'est DEUX putts : le nombre que le par d'un trou
 *  réserve au green. Au-delà, on perd un coup qui ne se rattrape pas
 *  ailleurs — d'où la coupure à deux plutôt qu'une moyenne seule, qui
 *  noierait les trois-putts dans les un-putt. */
export interface PuttingStats {
    holes: number;
    /** Trous en deux putts ou moins. */
    regulation: number;
    onePutt: number;
    threePutt: number;
    avgPutts: number | null;
    lastPlayedAt: string | null;
    games: number;
    /** Le putter déclaré le plus récemment. `null` si aucun. */
    clubId: string | null;
    clubLabel: string | null;
}

/** Un club du sac actif qu'on n'a pas sorti depuis longtemps. */
export interface NeglectedClub {
    clubId: string;
    label: string;
    brand: string | null;
    nickname: string | null;
    category: ClubCategory;
    loftDeg: number | null;
    /** `null` : jamais joué. */
    lastPlayedAt: string | null;
}

/** Un point de la courbe : un JOUR d'entraînement, pas un exercice.
 *
 *  Deux exercices ne se comparent pas — une pause au backswing ne réussit pas
 *  comme un triple shot — donc une courbe par exercice mesure surtout dans
 *  quel ordre on les a faits. Sur une journée, le mélange est à peu près
 *  constant d'une fois sur l'autre, et la pente veut enfin dire quelque chose. */
export interface TrendPoint {
    /** `YYYY-MM-DD`, jour local du joueur, sans heure ni fuseau. */
    day: string;
    /** Nombre d'exercices terminés ce jour-là. */
    sessions: number;
    shots: number;
    /** `numeric` de Postgres : arrive en chaîne. */
    successRate: string | null;
}

/* L'indice à composition constante : l'écart à son propre ordinaire.
 *
 * Le taux d'une journée mesure surtout le PROGRAMME suivi ce jour-là — un
 * atelier dur, un club ingrat, et la courbe plonge sans que rien n'ait changé
 * dans le geste. Ici chaque atelier n'est comparé qu'à lui-même, avec le même
 * club : la difficulté s'annule dans la soustraction, et ce qui reste se lit en
 * points de taux. « +6 » veut dire six points de mieux que d'ordinaire, à
 * programme équivalent.
 *
 * Le calcul vit sur le serveur, dans `lib/composition.ts`. */
export interface PointComparable {
    day: string;
    balls: number;
    /** L'écart à l'ordinaire, en points de taux. */
    ecart: number;
    /** La demi-largeur de l'intervalle à 95 %, en points. Une journée dont
     *  l'écart tient dans sa marge n'a rien prouvé. */
    marge: number;
}

/** La courbe d'UN atelier, comparable à elle-même d'un bout à l'autre. */
export interface CourbeAtelier {
    cle: string;
    mode: string;
    exerciseName: string | null;
    balls: number;
    rate: number;
    points: { day: string; shots: number; rate: number }[];
}

export interface Stats {
    /** Nombre de séances par fenêtre — affiché, car il change la portée de
     *  tout ce qui suit. */
    windowSize: number;
    overview: {
        sessionsTotal: number;
        sessions30d: number;
        lastSessionAt: string | null;
        totalSeconds: number;
        shotsTotal: number;
        /** Balles centrées depuis le début, toutes séances confondues. */
        centerTotal: number;
        /** Clubs du sac actif, pour borner les recommandations. */
        bagClubs: number;
        streakWeeks: number;
    };
    recent: StatsWindow;
    previous: StatsWindow;
    clubs: StatsClub[];
    /** Jusqu'à trois clubs du sac actif délaissés depuis plus d'un mois. */
    /** `null` si le joueur n'a jamais fait de putting. */
    putting: PuttingStats | null;
    neglected: NeglectedClub[];
    /** Du plus ancien au plus récent, douze jours d'entraînement au maximum. */
    trend: TrendPoint[];
    /** La même fenêtre, mais à composition constante. Vide sous deux jours. */
    comparable: PointComparable[];
    /** Les ateliers les plus joués de la fenêtre, chacun suivi à part. */
    ateliers: CourbeAtelier[];
    /** Le dernier entraînement terminé, pour y retourner d'un geste.
     *  `null` tant qu'il n'y en a aucun. */
    lastSession: {
        id: string;
        startedAt: string;
        mode: string;
        exerciseName: string | null;
        shots: number;
        center: number;
    } | null;
}

/** Le fuseau part avec la requête : le découpage en jours se fait côté base,
 *  et seul le navigateur sait dans quel fuseau le joueur a tapé ses balles. Un
 *  nom inconnu du serveur retombe sur UTC sans faire échouer la page. */
export const getStats = () =>
    api<Stats>(`/training/stats?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`);

export const listModes = () =>
    api<{ modes: TrainingMode[] }>('/training/modes').then((r) => r.modes);

const CLE_SEANCE = 'seance-en-cours';

export interface EtatSeance { session: Session | null; series?: Series[] }

/* La séance en cours, du serveur ou du téléphone — même raisonnement que pour
 * un tour : la copie l'emporte tant que des balles attendent d'être portées,
 * sinon la réponse du serveur les effacerait le temps d'une synchronisation. */
export const getCurrent = async (): Promise<EtatSeance> => {
    if (await enAttenteSur('/training')) {
        const copie = await lire<EtatSeance>(MAGASIN_ETAT, CLE_SEANCE);
        if (copie) return copie;
    }
    try {
        const etat = await api<EtatSeance>('/training/current');
        signalerReseau(true);
        await garderSeance(etat);
        return etat;
    } catch (e) {
        if (e instanceof ApiError) throw e;
        signalerReseau(false);
        const copie = await lire<EtatSeance>(MAGASIN_ETAT, CLE_SEANCE);
        if (copie) return copie;
        throw e;
    }
};

/** Garde la séance sous la main, telle que l'écran la tient. */
export const garderSeance = (etat: EtatSeance) =>
    remplacer(MAGASIN_ETAT, etat, CLE_SEANCE).then(() => undefined);

/* ── Les écritures d'une séance passent par la FILE ──────────────────────────
 *
 * Aucune ne dépend plus d'une réponse du serveur : chacune range son écriture
 * sur le téléphone et rend TOUT DE SUITE l'état qui en résulte, calculé ici.
 *
 * Ce calcul est au même endroit que l'écriture, et pas dans l'écran, pour une
 * raison simple : ce sont deux façons de dire la même chose — ce que devient
 * la série — et les séparer, c'est se donner deux occasions de ne pas dire la
 * même. L'écran se contente de poser le résultat dans son état.
 *
 * Les identifiants sont tirés ici. Le serveur les accepte tels quels, ce qui
 * rend chaque objet adressable avant même d'exister sur le serveur : une balle
 * saisie sans réseau peut être corrigée, ou défaite, dans la foulée.
 */

/** Le prochain rang libre d'une série. Le MAXIMUM plus un, et non le nombre de
 *  balles : une balle retirée au milieu laisse un trou, et recompter les
 *  lignes redonnerait un rang déjà pris. */
const rangSuivant = (serie: Series): number =>
    serie.shots.reduce((n, s) => Math.max(n, s.shotIndex), 0) + 1;

export const startSession = async (body: {
    mode?: string; bagId?: string; startedAt?: string;
    location?: string | null; notes?: string | null;
    /** Exercice personnalisé joué. Tourne sur le moteur du mode libre. */
    customExerciseId?: string | null;
    /** Rattachement à une séance en cours : le rang dit quelle étape est
     *  couverte, sans quoi deux exercices identiques seraient indiscernables. */
    runId?: string | null;
    runPosition?: number | null;
}, contexte: {
    /** Ce que le serveur aurait dénormalisé dans sa réponse, et que l'écran a
     *  déjà sous la main : le nom du sac, et les règles de l'exercice. */
    bagName?: string;
    exercice?: {
        name: string; description: string | null; successLabel: string | null;
        criteria: string | null; ballsPerRound: number | null; rounds: number | null;
        clubTemplateIds: number[];
    } | null;
} = {}): Promise<{ session: Session }> => {
    const id = identifiant();
    const startedAt = body.startedAt ?? new Date().toISOString();
    await enfiler({
        id, cree: id, method: 'POST', path: '/training/sessions', body: { ...body, id },
        quoi: 'Ouverture d’une séance',
    });
    const e = contexte.exercice ?? null;
    const session: Session = {
        id,
        bagId: body.bagId ?? '',
        bagName: contexte.bagName ?? '',
        mode: body.mode ?? 'triple_shot',
        status: 'in_progress',
        startedAt,
        endedAt: null,
        location: body.location ?? null,
        notes: body.notes ?? null,
        exerciseName: e?.name ?? null,
        exerciseDescription: e?.description ?? null,
        exerciseSuccessLabel: e?.successLabel ?? null,
        exerciseCriteria: e?.criteria ?? null,
        exerciseBalls: e?.ballsPerRound ?? null,
        exerciseRounds: e?.rounds ?? null,
        exerciseClubTemplateIds: e?.clubTemplateIds ?? [],
    };
    /* La copie locale est mise à jour ICI, et pas seulement par l'écran qui a
     * demandé l'ouverture.
     *
     * Dès que cette écriture entre dans la file, `getCurrent` cesse d'interroger
     * le serveur et lit la copie : sinon la réponse d'un serveur qui n'a pas
     * encore reçu la balle effacerait celles qui attendent. Tant que l'écran
     * appelant gardait le résultat sous la main, cela ne se voyait pas. Il
     * suffit qu'il NAVIGUE — c'est ce que fait le déroulé d'une séance — pour
     * que l'écran d'arrivée relise une copie d'avant : l'exercice précédent,
     * ou aucun, donc le catalogue.
     *
     * L'état qui résulte d'une écriture se range donc avec elle, comme pour
     * toutes les autres de ce module. */
    await garderSeance({ session, series: [] });
    return { session };
};

/** Les deux bornes sont modifiables : la durée n'est pas stockée, elle s'en
 *  déduit. Envoyer `startedAt` seul décale la fin d'autant, ce qui préserve la
 *  durée ; envoyer `endedAt` la fixe. */
export const patchSession = (id: string, body: {
    startedAt?: string; endedAt?: string; location?: string | null; notes?: string | null;
}) => enfiler({
    method: 'PATCH', path: `/training/sessions/${id}`, body,
    quoi: 'Modification d’une séance',
}).then(() => undefined);

/** Supprime une séance passée, avec ses séries, ses coups et ses agrégats. */
export const deleteSession = (id: string) =>
    enfiler({
        method: 'DELETE', path: `/training/sessions/${id}`,
        quoi: 'Suppression d’une séance',
    }).then(() => undefined);

/* `aborted` ne termine pas la séance : il l'EFFACE, séries et coups compris.
 *
 * Les balles qui attendaient encore dans la file n'ont donc plus d'objet — la
 * série à laquelle elles s'adressent va disparaître. On les jette ici, sans
 * cérémonie : le joueur vient de dire que rien de tout cela n'a eu lieu.
 *
 * Terminer, à l'inverse, ne jette rien : la séance reste en base, et le serveur
 * accepte les balles qui arrivent après coup — c'est même à cela que sert la
 * file. */
export const finishSession = async (
    id: string, status: 'completed' | 'aborted',
): Promise<void> => {
    if (status === 'aborted') {
        const jetees = await purgerDependants(id);
        // Séance jamais partie : il n'y a rien à effacer au serveur.
        if (jetees.some((e) => e.cree === id)) return;
    }
    await enfiler({
        method: 'POST', path: `/training/sessions/${id}/finish`, body: { status },
        quoi: status === 'aborted' ? 'Abandon d’une séance' : 'Fin d’une séance',
    });
    /* Plus rien en cours, et la copie doit le dire : elle fait autorité tant que
     * la file n'est pas vide, et laissée telle quelle elle rouvrirait le pavé de
     * saisie sur un exercice qu'on vient de terminer. */
    await garderSeance({ session: null, series: [] });
};

export const startSeries = async (
    sessionId: string, clubId: string, seriesIndex: number,
): Promise<{ series: Series }> => {
    const id = identifiant();
    await enfiler({
        id, cree: id, method: 'POST', path: `/training/sessions/${sessionId}/series`,
        body: { clubId, id },
        quoi: 'Ouverture d’une série',
    });
    return { series: { id, seriesIndex, clubId, completedAt: null, shots: [] } };
};

export const recordShot = async (
    serie: Series, outcome: ShotOutcome, distanceM?: number | null,
    /** Le jeton de la balle, rejoué tel quel après un échec réseau : le serveur
     *  rend alors la balle déjà écrite au lieu d'en ajouter une seconde. */
    clientToken?: string | null,
): Promise<{ series: Series }> => {
    const id = identifiant();
    const shotIndex = rangSuivant(serie);
    await enfiler({
        id, method: 'POST', path: `/training/series/${serie.id}/shots`,
        body: {
            id, outcome, distanceM: distanceM ?? null,
            ...(clientToken ? { clientToken } : {}),
        },
        quoi: `Balle ${shotIndex}`,
    });
    return {
        series: {
            ...serie,
            shots: [...serie.shots, { id, shotIndex, outcome, distanceM: distanceM ?? null }],
        },
    };
};

/** Ferme une série libre — les modes à taille fixe se ferment tout seuls. */
export const completeSeries = async (serie: Series): Promise<{ series: Series }> => {
    await enfiler({
        method: 'POST', path: `/training/series/${serie.id}/complete`,
        quoi: 'Fin d’une série',
    });
    return { series: { ...serie, completedAt: serie.completedAt ?? new Date().toISOString() } };
};

/** Supprime une série encore vide : club ouvert par erreur. */
export const discardSeries = (seriesId: string) =>
    enfiler({
        method: 'DELETE', path: `/training/series/${seriesId}`,
        quoi: 'Série abandonnée',
    }).then(() => undefined);

/** Corrige le club d'une série entamée — le fer 8 pris pour le 7. */
export const changeSeriesClub = async (
    serie: Series, clubId: string,
): Promise<{ series: Series }> => {
    await enfiler({
        method: 'PATCH', path: `/training/series/${serie.id}`, body: { clubId },
        quoi: 'Club corrigé',
    });
    return { series: { ...serie, clubId } };
};

/** Corrige une balle déjà saisie : son issue, sa distance, ou les deux. */
export const updateShot = async (
    serie: Series, shotId: string,
    patch: { outcome?: ShotOutcome; distanceM?: number | null },
): Promise<{ series: Series }> => {
    await enfiler({
        method: 'PATCH', path: `/training/shots/${shotId}`, body: patch,
        quoi: 'Balle corrigée',
    });
    return {
        series: {
            ...serie,
            shots: serie.shots.map((s) => (s.id === shotId ? { ...s, ...patch } : s)),
        },
    };
};

/** Supprime une balle, où qu'elle soit dans la série. */
export const deleteShot = async (
    serie: Series, shotId: string,
): Promise<{ series: Series }> => {
    await enfiler({
        method: 'DELETE', path: `/training/shots/${shotId}`,
        quoi: 'Balle supprimée',
    });
    return { series: { ...serie, shots: serie.shots.filter((s) => s.id !== shotId) } };
};

export const undoLastShot = async (serie: Series): Promise<{ series: Series }> => {
    // LA dernière au sens du rang, pas de l'ordre d'arrivée : après une
    // suppression au milieu, les deux ne coïncident plus.
    const dernier = serie.shots.reduce<Shot | null>(
        (a, b) => (a === null || b.shotIndex > a.shotIndex ? b : a), null,
    );
    await enfiler({
        method: 'DELETE', path: `/training/series/${serie.id}/shots/last`,
        quoi: 'Dernière balle défaite',
    });
    return {
        series: {
            ...serie,
            shots: dernier ? serie.shots.filter((s) => s.id !== dernier.id) : serie.shots,
        },
    };
};

export const getSummary = (sessionId: string) =>
    api<Summary>(`/training/sessions/${sessionId}/summary`);

export const listSessions = () =>
    api<{ sessions: HistoryItem[] }>('/training/sessions').then((r) => r.sessions);

/** Format `datetime-local` : l'input HTML veut une heure LOCALE sans fuseau,
 *  alors que l'API parle en ISO avec décalage. Passer directement une chaîne
 *  ISO UTC à l'input décale l'affichage de deux heures en été. */
export function toLocalInput(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Retour : ISO avec le décalage local, que l'API sait lire. */
export function fromLocalInput(value: string): string {
    return new Date(value).toISOString();
}
